<template>
  <div class="flex flex-col gap-y-1">
    <div :class="direction" class="w-full flex gap-x-4 gap-y-1">
      <label v-if="label" :for="fieldName" :class="labelClass" class="flex items-center gap-x-2">
        <div>
          <span> {{ t9nLabel }}</span>
          <span v-if="isRequired" class="text-brand-red ps-1">*</span>
        </div>
        <HintTippy v-if="hint" :hint="hint"></HintTippy>
      </label>
      <div class="w-full flex">
        <div
          :class="[{ 'border-s-4 border-s-primary': isRequired }]"
          class="p-1 w-2/5 flex items-center bg-gray-100 border border-brand-grayblue-300 rounded-s-sm"
        >
          <tippy
            :arrow="false"
            theme="light-border"
            interactive
            placement="bottom-start"
            trigger="focus click"
            class="w-full flex items-center"
            max-width="12rem"
          >
            <div
              class="w-full flex justify-between px-3"
              @click="handleClick($event)"
              :class="{ 'cursor-not-allowed': prefixDisabled }"
            >
              <button
                class="tippy__trigger w-full text-start line-clamp-1 text-xs truncate outline-none"
                :class="{ 'cursor-not-allowed text-gray-400': isPrefixDisabled }"
                :disabled="isPrefixDisabled"
                @click.prevent=""
              >
                <span v-if="selectedValue?.label" class="w-full">
                  {{ selectedValue?.label }}
                </span>
                <span v-else class="w-full text-gray-400">
                  {{ placeholder || t9nLabel }}
                </span>
              </button>
              <span>
                <FontAwesomeIcon :icon="faCaretDown" class="text-brand-black-secondary" />
              </span>
            </div>
            <template #content="{ hide }">
              <div class="tippy-content__container w-full p-0.5">
                <div class="w-full flex flex-col gap-y-1">
                  <div class="px-1.5 pt-1">
                    <input
                      v-model="searchValue"
                      type="text"
                      class="w-full ps-3 py-1.5 rounded-sm border border-brand-grayblue-300 outline-none focus:border-primary"
                    />
                  </div>
                  <div class="max-h-[12rem] h-full overflow-y-auto">
                    <ul class="divide-y pb-2">
                      <li v-for="(option, key) in filteredOptions" :key="key" class="">
                        <button
                          class="ps-3 py-1.5 hover:bg-gray-100 w-full text-start"
                          @click.prevent="
                            () => {
                              hide()
                              onSelection(option)
                            }
                          "
                        >
                          {{ option.label }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>
          </tippy>
        </div>
        <div class="flex-1">
          <input
            type="text"
            :disabled="withIsDisabled"
            :placeholder="placeholder || t9nLabel"
            :maxlength="maxLength || maxLengthFromRules"
            class="w-full ps-3 py-1.5 border border-brand-grayblue-300 outline-none focus:border-primary disabled:cursor-not-allowed"
            v-model="textModel"
            @input="handleInputChange"
            @blur="handleBlur"
          />
        </div>
      </div>
    </div>
    <div v-if="hasError" class="w-full flex flex-col md:flex-row gap-x-4 gap-y-1 md:items-center">
      <div v-if="showErrorSpacer" class="w-full md:w-1/3"></div>
      <span class="w-full pt-[2px] text-brand-red ltr:font-sans text-xs">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
import HintTippy from '@/components/Form/HintTyppy.vue'
import useRuleHandler from '@/composables/useRuleHandler'
import { useLookupsStore } from '@/stores/lookups'
import Fuse from 'fuse.js'
import { useField } from 'vee-validate'
import { computed, onMounted, ref, toRef, watch, watchEffect, useAttrs } from 'vue'
import { useI18n } from 'vue-i18n'
import { Tippy } from 'vue-tippy'
import { faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { useFormStore } from '@/stores/form'
import lodashGet from 'lodash/get'
import useVisible from '@/composables/useVisible'
import { useRoute } from 'vue-router'
import useWatchAction from '@/composables/watchActions/index'

const props = defineProps({
  // general props
  fieldName: {
    type: String,
    required: true
  },
  label: {
    type: String,
    required: true
  },
  isDisabled: Boolean,
  prefixDisabled: Boolean,
  hint: String,

  options: {
    type: [Array, Object],
    required: true
  },
  defaultValue: {
    type: [Object, String]
  },
  rules: Object,
  maxLength: Number,
  fuzzyKeywords: {
    type: Array,
    default: () => ['label']
  },
  direction: {
    type: String,
    default: 'flex-row md:items-center flex-col md:flex-row'
  },
  labelClass: {
    type: String,
    default: () => 'w-full md:w-1/3'
  },
  showErrorSpacer: {
    type: Boolean,
    default: () => true
  },
  placeholder: String,
  trigger: String,
  onWatch: Object,
  watchAction: String
})

const attrs = useAttrs()
const lookupStore = useLookupsStore()
const formStore = useFormStore()

const textModel = ref()
const selectedValue = ref()
const searchValue = ref()
const fuse = ref()
const selectOptions = ref()
const fieldName = toRef(props, 'fieldName')
const i18n = useI18n()
const route = useRoute()

const isPrefixDisabled = computed(() => attrs.disabled_prefix || withIsDisabled.value)

const { findShowRule, getBindObj } = useVisible()

const handleClick = (e) => {
  if (isPrefixDisabled.value) {
    e.stopPropagation()
    e.preventDefault()
  }
}

const maxLengthFromRules = computed(() => {
  const maxChar = props.rules?.find((r) => r.name == 'max_char')
  if (maxChar) {
    return maxChar.value
  }

  return null
})

if (props.trigger == 'watch') {
  try {
    useWatchAction()[props.onWatch.action]()
  } catch (error) {
    console.warn(`[comboText Watch trigger] ${props.onWatch.action} not found.`)
  }
}

if (props.watchAction) {
  try {
    useWatchAction()[props.watchAction]()
  } catch (error) {
    console.warn(`[comboText WatchAction trigger] ${props.onWatch.action} not found.`)
  }
}

onMounted(async () => {
  // check if props.options is already an array. if so, pass it
  if (Array.isArray(props.options)) {
    selectOptions.value = props.options
  } else {
    // otherwise call the api
    const { path } = props.options.api
    const options = await lookupStore.fetchLookup(path)

    /**
     * follow the structure. otherwise,
     * fetchLookup will provide the default label and value
     */
    if (props.options.structure) {
      const { label, value } = props.options.structure

      // set the label. if there's anything special, do an else-if
      if (label == 'diallingCode - localizedName') {
        Object.assign(
          options,
          options.map((o) => {
            return {
              ...o,
              label: `${o.diallingCode} - ${o.localizedName}`
            }
          })
        )
      } else {
        // otherwise set it normally
        Object.assign(
          options,
          options.map((o) => {
            return {
              ...o,
              label: o[label]
            }
          })
        )
      }

      Object.assign(
        options,
        options.map((o) => {
          return {
            ...o,
            value: o[value]
          }
        })
      )

      selectOptions.value = options
    }
  }

  /**
   * if there's a default value and there's a value in the draft.request object,
   * parse that value and set the code and text
   */
  if (props.defaultValue) {
    if (props.defaultValue.startsWith('00971') && props.defaultValue.length > 5) {
      const pattern = /(00971)(.*)/
      const [_, code, phone] = props.defaultValue.match(pattern)
      selectedValue.value = selectOptions.value.find((o) => o.value == code)
      textModel.value = phone
      // formStore.form.setFieldValue(props.fieldName, selectedValue.value.value)
      // handleChange(selectedValue.value.value, false)
      return
    }

    selectedValue.value = selectOptions.value.find((o) => o.value == props.defaultValue)
    if (props.web?.is_two_way && selectedValue.value) {
      textModel.value = selectedValue.value.value
    }
    // This line will trigger validate and it will show error when page initial,Now comment it,but not sure after comment it will cause any issue.
    // formStore.form.setFieldValue(props.fieldName, selectedValue.value.value)
  }
  fuse.value = new Fuse(selectOptions.value, {
    includeScore: true,
    threshold: 0.2,
    keys: props.fuzzyKeywords
  })
})

const { errorMessage, handleChange, handleBlur, meta } = useField(
  fieldName,
  (value) => useRuleHandler(value, props.rules),
  {
    initialValue: props.initialValue
  }
)

const fullOutput = computed(() => {
  const { diallingCode } = selectedValue.value
  return `${diallingCode}${textModel.value}`
})

const hasError = computed(() => errorMessage.value && meta.touched)

const filteredOptions = computed(() => {
  if (!searchValue.value) return selectOptions.value
  const result = fuse.value.search(searchValue.value)

  return result.map((r) => r.item)
})

const withIsDisabled = computed(() => {
  if (typeof props.isDisabled === 'boolean') {
    return props.isDisabled
  } else if (props.isDisabled && Object.keys(props.isDisabled).length > 0) {
    const result = findShowRule(
      props.isDisabled.any || props.isDisabled.all,
      props.isDisabled.any ? 'any' : 'all',
      getBindObj(route.params.serviceId)
    )

    return result
  } else {
    return false
  }
})

watchEffect(() => {
  getBindObj(route.params.serviceId)
})

const onSelection = (option) => {
  selectedValue.value = option
  if (!textModel.value || textModel.value == '') {
    return handleChange(null)
  }

  handleChange(fullOutput.value)
}

watch(textModel, () => {
  textModel.value && handleBlur()
  if (!textModel.value || textModel.value == '') {
    return handleChange(null)
  }

  handleChange(fullOutput.value)
})

// Synchronize pinia data and selected values
watch(
  () => lodashGet(formStore.form.values, props.fieldName),
  (val) => {
    if (!val) {
      textModel.value = null
    } else {
      filteredOptions.value.find((option) => {
        if (val.startsWith(option.value)) {
          selectedValue.value = option
          textModel.value = val.slice(option.value.length)
        }
      })
    }
  }
)

const t9nLabel = computed(() => i18n.t(props.label))

// const isRequired = computed(() => !!props.rules?.find((r) => r.name == 'required' || 'required_ui'))
const isRequired = computed(() => {
  const requiredReactive = props.rules?.find((r) => r.name == 'required_reactive')
  if (requiredReactive) {
    return isRequiredByReactive(requiredReactive)
  }
  return !!props.rules?.find((r) => ['required', 'required_ui'].includes(r.name))
})

/**
 * provides support for the required rule based on certain values
 * reason:
 * due to sharing forms, it's inevitable to have clashing logic on
 * some of the schema. this is a way to support both while still
 * enabling sharing between services.
 * {
 *     "name": "required_reactive",
 *     "value": {
 *         "operator": "in",
 *         "value": [
 *             33
 *         ],
 *         "ref": "serviceTransactionId"
 *     },
 *     "message": "INVALID_REQUIRED"
 * }
 */
const isRequiredByReactive = (rule) => {
  const { operator, value, ref } = rule.value

  let compareAgainst
  if (ref == 'serviceTransactionId') {
    compareAgainst = +route.params.serviceId
  } else {
    compareAgainst = formStore.form.values[ref]
  }

  switch (operator) {
    case 'in':
      return value.includes(compareAgainst)
    case 'not_in':
    case 'notIn':
      return [undefined, ...value].includes(compareAgainst) === false
    case 'not_null':
      return !!compareAgainst
    default:
      return false
  }
}

const handleInputChange = (e) => {
  const isNumberOnly = props.rules?.some((r) => r.name == 'numberOnly')

  if (isNumberOnly) {
    const allowedRegex = /^[0-9]*$/
    const cleaned = e.target.value
      .split('')
      .filter((char) => allowedRegex.test(char))
      .join('')
    e.target.value = cleaned
  }
}
</script>

<style lang="scss" scoped></style>
